import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { CnResult } from '@cainiaofe/cn-ui';
import { CnRanking } from '@/components/cn-ranking/index-pc';
import { isDesignMode } from '@/lowcode/common/util/util';
import useStopLoop from '@/lowcode/common/hooks/useStopLoop';
import { useData } from '@/lowcode/common/hooks/useData';
import { CnRankingLowCodeProps } from '@/components/cn-ranking/types';
import { getConfigFromLowCode } from '@/components/cn-ranking/utils';
import useFilterSearchListener from '../common/hooks/useFilterSearchListener';
import { EmptyData } from '../common/empty-data';
import WrapperContainer from '../common/wrapper-container';

const CnRankingPro = forwardRef(
  (props: CnRankingLowCodeProps, ref: React.ForwardedRef<{ load: () => void }>) => {
    const {
      dataFrom,
      chartStyle,
      filterConfig,
      forceRequest = false,
      _nodeId,
      _context,
    } = props || {};

    const { requestConfig } = dataFrom || {};

    const rankingConfig = getConfigFromLowCode(requestConfig);

    const {
      data: listData,
      error,
      loading,
      refreshData: sendRequest,
    } = useData({
      config: { ...props, isDesign: isDesignMode(props) },
      source: 'chart',
    });

    const { _bindFilter } = filterConfig || {};
    useFilterSearchListener({
      _bindFilter,
      sendRequest,
      _context,
    });

    useStopLoop({
      _nodeId,
    });

    useEffect(() => {
      if (forceRequest) {
        sendRequest();
      }
    }, [forceRequest]);

    useImperativeHandle(ref, () => ({
      load: sendRequest,
    }));

    const isConfigReady =
      rankingConfig.rankingDimension &&
      Array.isArray(rankingConfig.rankingMeasures) &&
      rankingConfig.rankingMeasures.length > 0;
    if (!isConfigReady || !rankingConfig.rankingDimension) {
      return (
        <WrapperContainer loading={loading} error={error}>
          <EmptyData
            height={chartStyle?.maxHeight || 200}
            emptyImg={
              'https://gw.alicdn.com/imgextra/i3/O1CN015Mn27K1c529TSS811_!!6000000003548-2-tps-1000-508.png'
            }
          />
        </WrapperContainer>
      );
    }

    const isDataReady = Array.isArray(listData) && listData.length > 0;
    if (!isDataReady) {
      return (
        <WrapperContainer loading={loading} error={error}>
          <CnResult type="empty" title="数据为空" subTitle="暂无数据" />;
        </WrapperContainer>
      );
    }

    return (
      <WrapperContainer loading={loading} error={error}>
        <CnRanking
          {...chartStyle}
          data={listData}
          rankingDimension={rankingConfig?.rankingDimension}
          rankingMeasures={rankingConfig?.rankingMeasures || []}
        />
      </WrapperContainer>
    );
  },
);

CnRankingPro.displayName = 'CnRankingPro';
export default CnRankingPro;

export { CnRankingPro };
